// صفحه تأییدها (AP-IAM-03 §5؛ US-IAM-034): صف «در انتظار تصمیم من» (درخواست‌هایی که سقف من آن‌ها را پوشش می‌دهد) با تأیید و رد،
// و «درخواست‌های من» با وضعیت (در انتظار، تأییدشده با اعتبار ۱۵ دقیقه، ردشده، استفاده‌شده، منقضی). مجوز تصمیم بر اساس سقف
// است نه Permission (C-AGENT-387)؛ اعلان درون‌برنامه‌ای درخواست تازه با ماژول پیام (فاز بعد).
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { unwrap, type ApprovalView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  JalaliDate,
  ListPage,
  ReasonDialog,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApprovalLimitValue } from './ApprovalLimitsTab';
import { iamKeys } from './common';

export const APPROVALS_PATH = '/approvals';
/** بخش «تأییدها»ی SCR-STAFF-404 (صف عمومی IAM؛ استردادها با AP-BIL-02 — C-AGENT-387). */
export const SCR_STAFF_APPROVALS = 'SCR-STAFF-404';

type Scope = 'inbox' | 'mine';

function statusTone(status: string): StatusTone {
  switch (status) {
    case 'Pending':
      return 'pending';
    case 'Approved':
      return 'active';
    case 'Consumed':
      return 'done';
    case 'Denied':
      return 'cancelled';
    default:
      return 'inactive';
  }
}

export function ApprovalsPage() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('inbox');
  const [approving, setApproving] = useState<ApprovalView | null>(null);
  const [denying, setDenying] = useState<ApprovalView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const approvals = useQuery({
    queryKey: iamKeys.approvals(session?.tenantId, scope),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/approvals', { params: { query: { scope } } })),
  });

  const decide = useMutation({
    mutationFn: ({ approval, approve, reason }: { approval: ApprovalView; approve: boolean; reason?: string }) => {
      const options = { params: { path: { id: approval.approvalId } }, body: { reason: reason ?? null } };
      return unwrap(
        approve
          ? tenantApi.POST('/api/v1/identity/approvals/{id}:approve', options)
          : tenantApi.POST('/api/v1/identity/approvals/{id}:deny', options),
      );
    },
    onSuccess: (_view, { approve }) => {
      setApproving(null);
      setDenying(null);
      setNotice(t(approve ? 'iam:approvals.approved' : 'iam:approvals.denied'));
      void queryClient.invalidateQueries({ queryKey: ['identity', session?.tenantId, 'approvals'] });
    },
  });

  const columns: DataTableColumn<ApprovalView>[] = [
    {
      id: 'requested_at',
      headerKey: 'iam:approvals.columns.requested_at',
      cell: (row) => <JalaliDate value={row.requestedAt} withTime />,
    },
    {
      id: 'requester',
      headerKey: 'iam:approvals.columns.requester',
      cell: (row) => row.requestedByName ?? t('common:states.none'),
    },
    {
      id: 'key',
      headerKey: 'iam:approvals.columns.key',
      cell: (row) => t(`iam:approval_limits.keys.${row.key}`, { defaultValue: row.key }),
    },
    {
      id: 'amount',
      headerKey: 'iam:approvals.columns.amount',
      cell: (row) => (
        <ApprovalLimitValue unit={row.key.endsWith('percent') ? 'Percent' : 'Amount'} value={row.amount} />
      ),
    },
    {
      id: 'reason',
      headerKey: 'iam:approvals.columns.reason',
      cell: (row) => row.reason ?? t('common:states.none'),
    },
    {
      id: 'status',
      headerKey: 'iam:approvals.columns.status',
      cell: (row) => (
        <span data-testid={`approval-status-${row.approvalId}`} data-status={row.status}>
          <StatusChip tone={statusTone(row.status)} labelKey={`iam:approvals.status.${row.status}`} />
        </span>
      ),
    },
  ];
  if (scope === 'inbox') {
    columns.push({
      id: 'actions',
      headerKey: 'iam:staff.columns.actions',
      cell: (row) =>
        row.canDecide ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                decide.reset();
                setApproving(row);
              }}
              data-testid={`approval-approve-${row.approvalId}`}
            >
              {t('iam:approvals.approve')}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => {
                decide.reset();
                setDenying(row);
              }}
              data-testid={`approval-deny-${row.approvalId}`}
            >
              {t('iam:approvals.deny')}
            </Button>
          </Stack>
        ) : null,
    });
  }

  return (
    <ListPage screenId={SCR_STAFF_APPROVALS} titleKey="iam:approvals.title" subtitleKey="iam:approvals.subtitle">
      <Tabs value={scope} onChange={(_, value: Scope) => setScope(value)} sx={{ mb: 2 }}>
        <Tab value="inbox" label={t('iam:approvals.inbox')} data-testid="approvals-inbox" />
        <Tab value="mine" label={t('iam:approvals.mine')} data-testid="approvals-mine" />
      </Tabs>
      <DataTable
        captionKey="iam:approvals.title"
        columns={columns}
        rows={approvals.data?.items}
        getRowId={(row) => row.approvalId}
        isLoading={approvals.isPending}
        error={approvals.error}
        onRetry={() => void approvals.refetch()}
        emptyTitleKey="iam:approvals.empty"
      />
      <ConfirmDialog
        open={approving !== null}
        titleKey="iam:approvals.approve"
        confirmKey="iam:approvals.approve"
        busy={decide.isPending}
        error={decide.error ?? undefined}
        onConfirm={() => approving && decide.mutate({ approval: approving, approve: true })}
        onCancel={() => setApproving(null)}
      />
      <ReasonDialog
        open={denying !== null}
        titleKey="iam:approvals.deny_title"
        confirmKey="iam:approvals.deny"
        busy={decide.isPending}
        error={decide.error ?? undefined}
        onConfirm={(reason) => denying && decide.mutate({ approval: denying, approve: false, reason })}
        onCancel={() => setDenying(null)}
      />
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </ListPage>
  );
}
